import { useEffect, useMemo, useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import { createPortal } from 'react-dom'

import { csrfToken } from '../../../utils/csrfToken'
import { Avatar } from '../../../utils/avatar'
import { initTooltips } from '../../../utils/tooltips'

function uniqueById(users) {
  const map = new Map()
  users.forEach((u) => {
    if (u && u.id) map.set(String(u.id), u)
  })
  return Array.from(map.values())
}

function AvatarGroup({ users, max = 999 }) {
  if (!users || users.length === 0) return <div className="">No views</div>

  const visible = users.slice(0, max)
  const hiddenCount = Math.max(0, users.length - visible.length)

  return (
    <div className="avatar-group d-inline-flex align-items-center">
      {visible.map((u) => (
        <span
          key={u.id}
          className="avatar avatar-xs"
          aria-label={u.full_name || u.email}
          data-bs-toggle="tooltip"
          data-bs-title={u.full_name || u.email}
        >
          <img
            src={u.avatar_url}
            className="avatar-img rounded-circle"
            alt={u.full_name || u.email}
          />
        </span>
      ))}
      {hiddenCount > 0 ? <span className="small text-muted ms-2">+ {hiddenCount} more</span> : null}
    </div>
  )
}

function submittedAtLabel(applicableDate, createdAtIso) {
  const createdAt = new Date(createdAtIso)
  const endOfDay = new Date(applicableDate)
  endOfDay.setDate(endOfDay.getDate() + 1)
  endOfDay.setHours(23, 59, 59, 999)
  if (createdAt <= endOfDay) return 'within 1 day'

  const diffMs = createdAt.getTime() - applicableDate.getTime()
  const mins = Math.round(diffMs / 60000)
  if (mins < 60) return `after ${mins} minutes`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `after ${hours} hours`
  const days = Math.round(hours / 24)
  return `after ${days} days`
}

async function fetchOnlineUsers() {
  const response = await fetch('/users/online.json', {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  })
  if (!response.ok) throw new Error(`Failed to load online users: ${response.status}`)
  return response.json()
}

function applyUserFilterToData(data, selectedUserId) {
  if (!data) return data
  if (!selectedUserId) return data

  const idStr = String(selectedUserId)
  const nextDays = data.days.map((day) => {
    const reports = (day.reports || []).filter((r) => String(r.user?.id) === idStr)
    const viewers = uniqueById(reports.flatMap((r) => r.viewers || []))
    return {
      ...day,
      reports,
      reports_count: reports.length,
      viewers,
    }
  })

  return {
    ...data,
    days: nextDays,
    selected_user_id: selectedUserId,
  }
}

async function fetchDailyReport({ id }) {
  const response = await fetch(`/users/daily_reports/${id}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  })
  if (!response.ok) throw new Error(`Failed to load report: ${response.status}`)
  return response.json()
}

function submittedAtTimestamp(createdAtIso) {
  const createdAt = new Date(createdAtIso)
  if (Number.isNaN(createdAt.getTime())) return ''

  return new Intl.DateTimeFormat('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(new Date(createdAtIso))
}

function formatDateLabel(dateStr) {
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) return dateStr

  return new Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date)
}

function DailyReportOffcanvas({ open, onClose, loading, title, children }) {
  if (!open) return null

  const root = document.body
  return createPortal(
    <>
      <div
        className="offcanvas-backdrop fade show"
        role="presentation"
        onClick={onClose}
      />
      <div
        className={`offcanvas offcanvas-end w-50 show`}
        tabIndex={-1}
        style={{ visibility: 'visible' }}
        role="dialog"
        aria-modal="true"
      >
        <div className="offcanvas-header">
          <div className="flex-column">
            <h5 className="offcanvas-title">{loading ? 'Loading…' : title}</h5>
          </div>
          <button type="button" className="btn-close text-reset" aria-label="Close" onClick={onClose} />
        </div>
        <div className="offcanvas-body">{children}</div>
      </div>
    </>,
    root,
  )
}

export default function Index({ dailyReports, initialUserId, initialDailyReportId, isManager }) {
  const [rawData, setRawData] = useState(dailyReports)
  const loading = false
  const error = null
  const [selectedUserId, setSelectedUserId] = useState(initialUserId || null)
  const [onlineUsers, setOnlineUsers] = useState([])

  const [offcanvasOpen, setOffcanvasOpen] = useState(false)
  const [offcanvasLoading, setOffcanvasLoading] = useState(false)
  const [offcanvasTitle, setOffcanvasTitle] = useState('')
  const [offcanvasContentHtml, setOffcanvasContentHtml] = useState('')

  const data = useMemo(() => applyUserFilterToData(rawData, selectedUserId), [rawData, selectedUserId])

  const didOpenInitialReport = useRef(false)

  const daysByDate = useMemo(() => {
    if (!data?.days) return new Map()
    const m = new Map()
    data.days.forEach((d) => m.set(d.date, d))
    return m
  }, [data])

  const onlineUserIds = useMemo(() => new Set((onlineUsers || []).map((u) => String(u.id))), [onlineUsers])

  useEffect(() => {
    initTooltips(document)
  }, [rawData, onlineUsers])

  useEffect(() => {
    if (!initialDailyReportId) return
    if (!rawData) return
    if (didOpenInitialReport.current) return
    didOpenInitialReport.current = true
    handleOpenReport(initialDailyReportId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDailyReportId, rawData])

  useEffect(() => {
    let cancelled = false

    fetchOnlineUsers()
      .then((payload) => {
        if (cancelled) return
        setOnlineUsers(payload.users || [])
      })
      .catch(() => {})

    return () => {
      cancelled = true
    }
  }, [])

  async function handleOpenReport(id) {
    setOffcanvasOpen(true)
    setOffcanvasLoading(true)
    setOffcanvasTitle('')
    setOffcanvasContentHtml('')

    const payload = await fetchDailyReport({ id })
    const dr = payload.daily_report

    const dateLabel = new Intl.DateTimeFormat('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(new Date(dr.applicable_date))
    const submitted = submittedAtLabel(new Date(dr.applicable_date), dr.created_at)
    setOffcanvasTitle(`Daily Report for ${dateLabel} submitted ${submitted}`)
    setOffcanvasContentHtml(dr.content_html)
    setOffcanvasLoading(false)

    // Update in-memory state for "realtime" Seen By updates
    setRawData((prev) => {
      if (!prev) return prev
      const nextDays = prev.days.map((day) => {
        if (day.date !== dr.applicable_date) return day
        const nextReports = day.reports.map((r) => {
          if (String(r.id) !== String(dr.id)) return r
          return { ...r, viewers: dr.viewers }
        })
        const nextViewers = uniqueById(nextReports.flatMap((r) => r.viewers || []))
        return { ...day, reports: nextReports, viewers: nextViewers }
      })
      return { ...prev, days: nextDays }
    })
  }

  function navigateToNew(applicableDate) {
    router.visit(`/users/daily_reports/new?applicable_date=${encodeURIComponent(applicableDate)}`)
  }

  if (loading) return <div className="container">Loading…</div>
  if (error) return <div className="container">{error}</div>
  if (!data) return <div className="container">No data</div>

  const currentUserId = data.current_user_id ? String(data.current_user_id) : null

  const columns = [
    { key: 'Mon', label: 'Mon', grid: 'mon' },
    { key: 'Tue', label: 'Tue', grid: 'tue' },
    { key: 'Wed', label: 'Wed', grid: 'wed' },
    { key: 'Thu', label: 'Thu', grid: 'thu' },
    { key: 'Fri', label: 'Fri', grid: 'fri' },
    { key: 'Weekend', label: 'Sat/Sun', grid: 'weekend' },
  ]

  return (
    <>
      <div className="container-fluid">
        <div className="header">
          <div className="container-fluid">
            <div className="header-body">
              <div className="row align-items-end">
                <div className="col">
                  <h6 className="header-pretitle">Daily Reports</h6>
                  <h1 className="header-title">My Daily Reports</h1>
                </div>
                <div className="col-auto">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => router.visit('/users/daily_reports/new')}
                  >
                    Create Report
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container-fluid">
        {isManager && data.users?.length > 0 ? (
          <div className="container mb-4">
            <div className="avatar-filter m-auto">
              <h2>Filter</h2>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {data.users.map((u) => {
                  const active = selectedUserId && String(selectedUserId) === String(u.id)
                  return (
                    <button
                      key={u.id}
                      type="button"
                      className={`btn p-0 border-0 bg-transparent ${active ? 'is-active' : ''}`}
                      onClick={() => setSelectedUserId(active ? null : u.id)}
                      style={{ padding: 0 }}
                    >
                      <Avatar user={u} size="sm" online={onlineUserIds.has(String(u.id))} />
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        ) : null}

        <div className="container-fluid d-none d-lg-flex mb-4">
          <div
            className="m-auto"
            style={{
              overflow: 'hidden',
              display: 'grid',
              gap: '0.5rem',
              gridTemplateColumns: '[mon] 12rem [tue] 12rem [wed] 12rem [thu] 12rem [fri] 12rem [weekend] 12rem',
              gridTemplateRows: '[header] 1fr [body1] 12rem [body2] 12rem [body3] 12rem [body4] 12rem [body5] 12rem',
            }}
          >
            {columns.map((column) => (
              <div key={column.key} style={{ gridColumnStart: column.grid, gridRowStart: 'header' }}>
                <h3 className="m-0 p-3">{column.label}</h3>
              </div>
            ))}

            {columns.map((column) => {
              const dates = data.date_groups?.[column.key] || []
              return dates.map((dateStr, index) => {
                const day = daysByDate.get(dateStr)
                const reportsCount = day?.reports_count || 0
                const viewers = day?.viewers || []

                return (
                  <div
                    key={`${column.key}-${dateStr}`}
                    className="border d-flex flex-column p-3"
                    style={{ gridColumnStart: column.grid, gridRowStart: `body${index + 1}` }}
                  >
                    <h3>{day?.label || formatDateLabel(dateStr)}</h3>

                    {reportsCount === 0 ? (
                      day?.can_create ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={() => navigateToNew(dateStr)}
                        >
                          Add Report
                        </button>
                      ) : null
                    ) : (
                      <>
                        <span>
                          <a href={`#daily-report-${dateStr}`}>{reportsCount} reports</a>
                        </span>
                        <div className="mt-auto ml-auto text-end">
                          <AvatarGroup users={viewers} max={4} />
                        </div>
                      </>
                    )}
                  </div>
                )
              })
            })}
          </div>
        </div>
      </div>

      <div className="container-fluid">
        <div className="row justify-content-center">
          {data.days.map((day) => {
            if (!day || !day.date) return null
            if (day.reports_count === 0) return null
            return (
              <div key={day.date} className="col-12 col-md-10 col-xl-8">
                <div id={`daily-report-${day.date}`} className="card mb-4">
                  <div className="card-header">
                    <h3 className="m-0">{day.label}</h3>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-sm table-nowrap card-table">
                      <thead>
                        <tr>
                          <th style={{ width: '33%' }}>User</th>
                          <th>Submitted</th>
                          <th>Words</th>
                          <th>Seen By</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody className="fs-base">
                        {day.reports.map((r) => (
                          <tr key={r.id}>
                            <td>
                              {r.user?.full_name || r.user?.email}{' '}
                              {currentUserId && String(r.user?.id) === currentUserId ? '(You!)' : null}
                            </td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-link p-0"
                                onClick={() => handleOpenReport(r.id)}
                              >
                                {submittedAtLabel(new Date(r.applicable_date || day.date), r.created_at)}
                              </button>
                              <div className="small text-muted">{submittedAtTimestamp(r.created_at)}</div>
                            </td>
                            <td>{r.word_count}</td>
                            <td>
                              <AvatarGroup users={r.viewers || []} max={4} />
                            </td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={() => handleOpenReport(r.id)}
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <DailyReportOffcanvas
        open={offcanvasOpen}
        loading={offcanvasLoading}
        title={offcanvasTitle}
        onClose={() => setOffcanvasOpen(false)}
      >
        {offcanvasLoading ? (
          <div>Loading…</div>
        ) : (
          <div dangerouslySetInnerHTML={{ __html: offcanvasContentHtml }} />
        )}
      </DailyReportOffcanvas>
    </>
  )
}
